export type Listener<T> = (value: T) => void;

export interface ReadonlyObservable<T> {
  get(): T;
  /** Calls the listener on every change (not immediately). Returns an unsubscribe function. */
  subscribe(listener: Listener<T>): () => void;
}

/**
 * Minimal framework-agnostic observable value. The client core uses it so Preact components,
 * the world's view and the bot controller can all react to the same state.
 */
export class Observable<T> implements ReadonlyObservable<T> {
  #value: T;
  readonly #listeners = new Set<Listener<T>>();

  constructor(initial: T) {
    this.#value = initial;
  }

  get(): T {
    return this.#value;
  }

  set(next: T): void {
    if (Object.is(next, this.#value)) return;
    this.#value = next;
    for (const listener of [...this.#listeners]) listener(next);
  }

  update(fn: (current: T) => T): void {
    this.set(fn(this.#value));
  }

  subscribe(listener: Listener<T>): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }
}
